import type { ElementGroupState, GemMeterElement, RuleHandler, ScreenType, StandardState } from '../../types/types';
import RuleHandlers from '../rules';
import CHECK_LIST, {
  DIFFERENT_BUTTON_FONT_FAMILY,
  DIFFERENT_HEADING_FONT_FAMILY,
  SHOPABLE_PREFIX,
} from '../../constants/rulesConstants';
import {
  getConvertableStandard,
  getDataSetting,
  getElementFromStore,
  getElementGroup,
  getElementOfRule,
  getElementsOfRules,
  getScreenSize,
  getScreenType,
  getShopableStandards,
  getStandardByRuleId,
} from './useGemMeterStore';
import { getProductLatest } from '@/app/utils/assignedProductHelper';
import GemMeterBaseRule from '../rules/GemMeterBaseRule';

export function convertKeyToGroup(elementKey: string) {
  switch (elementKey) {
    case 'button':
    case 'p-cart-button':
    case 'p-view-more':
    case 'submit-button':
    case 'dynamic-button':
      return [CHECK_LIST.NO_CALL_TO_ACTION_FOUND.id];
    case 'product-list':
      return [CHECK_LIST.NO_CALL_TO_ACTION_FOUND.id];
    case 'loox-reviews-carousel':
    case 'judge-reviews':
    case 'judge-reviews-carousel':
    case 'product-reviews':
    case 'lai-aliexpress-reviews':
    case 'yotpo-reviews':
    case 'yotpo-featured':
    case 'loox':
    case 'ali-reviews':
    case 'growave':
    case 'stamped-product-reviews':
    case 'ryviu-product-reviews':
    case 'opinew-product-reviews':
    case 'rivyo-product-reviews':
      return [CHECK_LIST.WEAK_SENSE_OF_TRUST.id];
    default:
      return null;
  }
}

// Used to determine element belong to which standards
export function convertKeyToStandard(elementKey: string): string[] {
  switch (elementKey) {
    case 'heading':
      return [CHECK_LIST.TITLE_IS_TOO_LONG.id, CHECK_LIST.HARD_TO_READ_CONTENT.id];
    case 'text-field':
    case 'text-area':
    case 'email-field':
      return [CHECK_LIST.CAN_NOT_SEND_EMAIL.id];
    case 'p-description':
      return [CHECK_LIST.HARD_TO_READ_CONTENT.id];
    case 'button':
    case 'p-cart-button':
      return [
        CHECK_LIST.HARD_TO_READ_BUTTON.id,
        CHECK_LIST.HARD_TO_CLICK_BUTTON.id,
        CHECK_LIST.MISSING_LINK.id,
        CHECK_LIST.WEAK_CALL_TO_ACTION.id,
      ];
    case 'submit-button':
    case 'dynamic-button':
      return [
        CHECK_LIST.HARD_TO_READ_BUTTON.id,
        CHECK_LIST.HARD_TO_CLICK_BUTTON.id,
        CHECK_LIST.WEAK_CALL_TO_ACTION.id,
      ];
    case 'vimeo':
    case 'youtube':
    case 'video-bg':
    case 'video-popup':
      return [CHECK_LIST.NO_SOURCE_VIDEO.id];
    case 'text-block':
      return [CHECK_LIST.HARD_TO_READ_CONTENT.id];
    default:
      return [];
  }
}

export function createGemMeterElement(elementKey: string, elementMerId: string) {
  const fonts = getFonts(elementMerId, elementKey);
  return {
    key: elementKey,
    id: elementMerId.substring(3),
    merId: elementMerId,
    type: elementMerId.split('-').shift() === '#m' ? 'module' : 'element',
    isPassed: false,
    status: 'new',
    visible: true,
    fontFamily: fonts?.fontFamily,
    fontWeight: fonts?.fontWeight,
  } as GemMeterElement;
}

export const hasNotProductInShopifyStore = (): boolean => {
  const latestProduct: any = getProductLatest();
  return !latestProduct || !latestProduct?.id;
};

export function getRule(elementKey: string): RuleHandler | null {
  switch (elementKey) {
    case 'heading':
      return {
        additionalSelector: '.text-edit :first-child',
        handlers: [RuleHandlers.TextSizeRule, RuleHandlers.TitleTooLongRule, RuleHandlers.DifferentHeadingFontFamily],
      };
    case 'text-field':
    case 'email-field':
      return {
        additionalSelector: '.gf_textfield',
        handlers: [RuleHandlers.InputFieldNameRule],
      };
    case 'text-area':
      return {
        additionalSelector: '.gf_textarea',
        handlers: [RuleHandlers.InputFieldNameRule],
      };
    case 'text-block':
      return {
        additionalSelector: '.text-edit',
        handlers: [RuleHandlers.TextSizeRule],
      };
    case 'p-description':
      return {
        additionalSelector: '.gf_product-desc',
        handlers: [RuleHandlers.TextSizeRule],
      };
    case 'button':
    case 'p-view-more':
      return {
        additionalSelector: '.button',
        handlers: [
          RuleHandlers.ContrastRule,
          RuleHandlers.ButtonMinSizeRule,
          RuleHandlers.MissingButtonLink,
          RuleHandlers.ButtonTextLengthRule,
          RuleHandlers.DifferentButtonFontFamily,
        ],
      };
    case 'submit-button':
      return {
        additionalSelector: '.button',
        handlers: [
          RuleHandlers.ContrastRule,
          RuleHandlers.ButtonMinSizeRule,
          RuleHandlers.ButtonTextLengthRule,
          RuleHandlers.DifferentButtonFontFamily,
        ],
      };
    case 'dynamic-button':
      return {
        additionalSelector: '.shopify-payment-button__button',
        handlers: [
          RuleHandlers.ContrastRule,
          RuleHandlers.ButtonMinSizeRule,
          RuleHandlers.ButtonTextLengthRule,
          RuleHandlers.DifferentButtonFontFamily,
        ],
      }
    case 'p-cart-button': {
      if (hasNotProductInShopifyStore()) {
        return null;
      }

      return {
        additionalSelector: '.button',
        handlers: [
          RuleHandlers.ContrastRule,
          RuleHandlers.ButtonMinSizeRule,
          RuleHandlers.ButtonTextLengthRule,
          RuleHandlers.DifferentButtonFontFamily,
        ],
      };
    }
    case 'vimeo':
    case 'youtube':
      return {
        additionalSelector: '.module',
        handlers: [RuleHandlers.MissingMediaLinkRule],
      };
    case 'video-bg':
      return {
        additionalSelector: '.video-background',
        handlers: [RuleHandlers.MissingVideoBgLinkRule],
      };
    case 'video-popup': {
      return {
        additionalSelector: '.video-popup',
        handlers: [RuleHandlers.MissingPopupLinkRule],
      };
    }
    case 'count-down':
      return {
        additionalSelector: '.count-down-wrap',
        handlers: [RuleHandlers.CountdownTimerHourRule],
      };
    default:
      return null;
  }
}

export function updateStandardWithNewElement(
  standards: StandardState,
  ruleId: string,
  screenKey: 'mobile' | 'desktop',
  isValid: boolean,
  elementMerId: string,
  gemElement: GemMeterElement,
) {
  if (!standards.hasOwnProperty(ruleId)) {
    return standards;
  }

  if (!isValid) {
    standards[ruleId].counter[screenKey].errorCount += 1;
    standards[ruleId].errorElements[screenKey][elementMerId] = gemElement;
  } else {
    standards[ruleId].counter[screenKey].fixedCount += 1;
  }

  return standards;
}

export function updateStandardForGroupWithNewElement(
  standards: StandardState,
  ruleId: string,
  screenKey: 'mobile' | 'desktop',
  isValid: boolean,
) {
  if (!standards.hasOwnProperty(ruleId)) {
    return standards;
  }
  standards[ruleId].isPageValidate = true;
  if (standards[ruleId].counter[screenKey].skippedCount) {
    standards[ruleId].counter[screenKey].errorCount = 0;
    return standards;
  }
  if (!isValid) {
    standards[ruleId].counter[screenKey].errorCount = 1;
    standards[ruleId].counter[screenKey].fixedCount = 0;
  } else {
    if (standards[ruleId].counter[screenKey].skippedCount === 0) {
      standards[ruleId].counter[screenKey].fixedCount = 1;
    }
    standards[ruleId].counter[screenKey].errorCount = 0;
  }

  return standards;
}

export function updateStandardWhenRemoveElement(
  standards: StandardState,
  ruleId: string,
  screenKey: 'mobile' | 'desktop',
  elementMerId: string,
) {
  if (!standards.hasOwnProperty(ruleId)) {
    return standards;
  }

  const element = standards[ruleId].errorElements[screenKey][elementMerId];
  if (!element) {
    standards[ruleId].counter[screenKey].fixedCount -= 1;
  } else {
    switch (element.status) {
      case 'new':
        standards[ruleId].counter[screenKey].errorCount -= 1;
        break;
      case 'skip':
        standards[ruleId].counter[screenKey].skippedCount -= 1;
        break;
    }
  }

  delete standards[ruleId].errorElements[screenKey][elementMerId];

  return standards;
}

export function convertRuleByAttrName(attributeName: string) {
  switch (attributeName) {
    case 'editLink':
      return [CHECK_LIST.MISSING_LINK.id];
    case 'url':
    case 'vimeo-url':
    case 'videobg_youtube':
    case 'videobg_vimeo':
    case 'videobg_custom':
    case 'youtube-url':
    case 'video_source':
    case 'custom-url':
      return [CHECK_LIST.NO_SOURCE_VIDEO.id];
    case 'editText':
    case 'btnText':
      return [CHECK_LIST.WEAK_CALL_TO_ACTION.id, CHECK_LIST.HARD_TO_CLICK_BUTTON.id];
    case 'height':
      return [CHECK_LIST.HARD_TO_CLICK_BUTTON.id];
    case 'button-color':
    case 'color':
    case 'text-color':
    case 'fontWeight':
    case 'background-color':
      return [CHECK_LIST.HARD_TO_READ_BUTTON.id];
    case 'font-size':
      return [
        CHECK_LIST.HARD_TO_READ_CONTENT.id,
        CHECK_LIST.HARD_TO_CLICK_BUTTON.id,
        CHECK_LIST.HARD_TO_READ_BUTTON.id,
      ];
    case 'name':
      return [CHECK_LIST.CAN_NOT_SEND_EMAIL.id];
    case 'datetime':
    case 'everGreen':
      return [CHECK_LIST.WEAK_SENSE_OF_URGENCY.id];
    case 'padding-top':
    case 'padding-bottom':
      return [CHECK_LIST.HARD_TO_CLICK_BUTTON.id];
    case 'title-length':
      return [CHECK_LIST.TITLE_IS_TOO_LONG.id];
  }

  return [];
}

export function checkElementByRule(element: any, ruleId: string) {
  let isValid = null;
  const rules = getRule(element.key);

  if (!rules) {
    return null;
  }

  const screenType = getScreenType();
  const selector = `${element.merId} ${rules.additionalSelector}`;
  for (const handler of rules.handlers) {
    if (handler.ruleId === ruleId) {
      isValid = handler.validate(selector, screenType);
      break;
    }
  }

  return isValid;
}

// Version 2 (Run only when add element)
export function updateElementGroup(
  elementGroups: ElementGroupState,
  screenType: ScreenType,
  elementKey: string,
  elementMerId: string,
) {
  const groupKeys = convertKeyToGroup(elementKey);
  if (groupKeys === null) {
    return elementGroups;
  }

  groupKeys.forEach((key) => {
    const gemElement = createGemMeterElement(elementKey, elementMerId);
    if (!elementGroups[screenType].hasOwnProperty(key)) {
      elementGroups.lg[key][elementMerId] = gemElement;
      elementGroups.md[key][elementMerId] = gemElement;
      elementGroups.sm[key][elementMerId] = gemElement;
      elementGroups.xs[key][elementMerId] = gemElement;
    }
  });

  return elementGroups;
}

export function getRuleGroupByRuleId(key: string): { validate: Function; ruleId: string } | null {
  switch (key) {
    case CHECK_LIST.NO_CALL_TO_ACTION_FOUND.id:
      return RuleHandlers.NoCallToActionFoundRule;
    case CHECK_LIST.PAGE_HEIGHT_IS_TOO_LONG.id:
      return RuleHandlers.PageHightIsTooLongRule;
    case CHECK_LIST.WEAK_SENSE_OF_TRUST.id:
      return RuleHandlers.WeakSenseOfTrustRule;
    default:
      return null;
  }
}

export function getStandardsValidatePage(
  convertableStandards: StandardState,
  shopableStandards: StandardState,
  removeElementId?: string,
) {
  const rulesGroup = [
    CHECK_LIST.NO_CALL_TO_ACTION_FOUND,
    CHECK_LIST.PAGE_HEIGHT_IS_TOO_LONG,
    CHECK_LIST.WEAK_SENSE_OF_TRUST,
  ];
  rulesGroup.forEach((rule) => {
    const ruleGroup = getRuleGroupByRuleId(rule.id);
    const devices = rule.devices || ['lg', 'md', 'sm', 'xs'];
    if (ruleGroup) {
      const isValidate = ruleGroup.validate(removeElementId);
      if (rule.id.includes(SHOPABLE_PREFIX)) {
        devices.forEach((device) => {
          shopableStandards = updateStandardForGroupWithNewElement(
            shopableStandards,
            ruleGroup.ruleId,
            device,
            isValidate,
          );
        });
      } else {
        devices.forEach((device: any) => {
          convertableStandards = updateStandardForGroupWithNewElement(
            convertableStandards,
            ruleGroup.ruleId,
            device,
            isValidate,
          );
        });
      }
    }
  });

  return { convertableStandards, shopableStandards };
}

export function checkEmptyDataSettings(removeElementId: string | undefined) {
  const dataSetting = getDataSetting();
  const keys = Object.keys(dataSetting);

  if (!removeElementId) {
    return keys.length === 0;
  }

  if (keys.length === 0) {
    return true;
  }
  const removeElementKeys = keys.filter((key) => key.includes(removeElementId));

  return removeElementKeys.length === keys.length;
}

/**
 * Standalone rule will take care full process, includes:
 * - Validate rule
 * - Update store
 */
export function isStandaloneRule(ruleId: string) {
  return [DIFFERENT_HEADING_FONT_FAMILY.id, DIFFERENT_BUTTON_FONT_FAMILY.id].includes(ruleId);
}

export function getGroupLengthByRuleId(ruleId: string) {
  const elementGroups = getElementGroup();
  if (elementGroups[ruleId]) {
    return Object.keys(elementGroups[ruleId]).length;
  }

  return -1;
}

// Product list
export const getElementIdRecheck = (rootElementId: string, ruleId: string) => {
  const screenType = getScreenType();
  const standard = getStandardByRuleId(ruleId);
  const elementErrors = standard[ruleId]?.errorElements[screenType] || {};
  const regex = /-child(.*?)-/;

  if (rootElementId.match(regex)) {
    const splitString = rootElementId.split('-child');
    const splitStringForIndex = rootElementId.split('-');
    return Object.keys(elementErrors)
      .filter((item) => {
        const splitStringForIndexItem = item.split('-');
        return (
          item.includes(`${splitString[0]}-child`) &&
          splitStringForIndex[splitStringForIndex.length - 1] ===
            splitStringForIndexItem[splitStringForIndexItem.length - 1] &&
          splitStringForIndexItem.length - 1 === splitStringForIndex.length
        );
      })
      .map((item) => {
        return item;
      });
  }

  return [rootElementId];
};

export const getProductAndProductListIdsInGroup = (ruleKey: string) => {
  const screenType = getScreenType();
  const elementGroups = getElementGroup();
  const elements = elementGroups[screenType][ruleKey] || {};
  const keys = Object.keys(elements);

  return keys
    .map((key) => elements[key])
    .filter((item) => item.key === 'product' || item.key === 'product-list')
    .map((item) => item.merId);
};

export function updateStandardWhenReInit(
  standard: StandardState,
  ruleId: string,
  screenSize: 'desktop' | 'mobile',
  gemElement: GemMeterElement,
  isValid: boolean,
) {
  if (
    isValid &&
    standard[ruleId].errorElements[screenSize][gemElement.merId] &&
    standard[ruleId].errorElements[screenSize][gemElement.merId].status === 'new'
  ) {
    standard[ruleId].errorElements[screenSize][gemElement.merId].status = 'fixed';
    standard[ruleId].errorElements[screenSize][gemElement.merId].visible = false;
    standard[ruleId].counter[screenSize].errorCount -= 1;
    standard[ruleId].counter[screenSize].fixedCount += 1;
  } else if (
    !isValid &&
    standard[ruleId].errorElements[screenSize][gemElement.merId] &&
    standard[ruleId].errorElements[screenSize][gemElement.merId].status === 'new'
  ) {
    standard[ruleId].errorElements[screenSize][gemElement.merId].visible = true;
  } else if (!isValid && !standard[ruleId].errorElements[screenSize][gemElement.merId]) {
    standard[ruleId].errorElements[screenSize][gemElement.merId] = gemElement;
    standard[ruleId].counter[screenSize].errorCount += 1;
    standard[ruleId].counter[screenSize].fixedCount -= 1;
  } else if (
    !isValid &&
    standard[ruleId].errorElements[screenSize][gemElement.merId] &&
    standard[ruleId].errorElements[screenSize][gemElement.merId].status === 'fixed'
  ) {
    standard[ruleId].errorElements[screenSize][gemElement.merId].status = 'new';
    standard[ruleId].counter[screenSize].errorCount += 1;
    standard[ruleId].counter[screenSize].fixedCount -= 1;
    standard[ruleId].errorElements[screenSize][gemElement.merId].visible = true;
  }

  return standard;
}

export function createElementInfo() {
  return {
    lg: {
      visible: true,
    },
    md: {
      visible: true,
    },
    sm: {
      visible: true,
    },
    xs: {
      visible: true,
    },
  };
}

export function updateElementVisibility(elementId: string, screenType: ScreenType, isVisible: boolean) {
  const elementInfo = getElementOfRule(`#e-${elementId}`);
  // Ignore if element info not found or visible status not change
  if (!elementInfo || elementInfo[screenType].visible === isVisible) {
    return false;
  }

  const rawElement = getElementFromStore(elementId);
  // Ignore if element not found in Vuex store
  const element = JSON.parse(rawElement);
  if (!element) {
    return false;
  }

  const relatedRules = getRule(element.key);
  if (relatedRules) {
    let shopableStandards = getShopableStandards();
    let convertableStandards = getConvertableStandard();
    if (isVisible) {
      relatedRules.handlers.forEach((handler) => {
        const ruleId = handler.ruleId;
        if (ruleId.includes(SHOPABLE_PREFIX)) {
          shopableStandards = updateStandardWhenChangeVisibility(shopableStandards, ruleId, screenType, elementId);
        } else {
          convertableStandards = updateStandardWhenChangeVisibility(
            convertableStandards,
            ruleId,
            screenType,
            elementId,
          );
        }
      });
    } else {
    }
  }
}

function updateStandardWhenChangeVisibility(
  standard: StandardState,
  ruleId: string,
  screenType: 'desktop' | 'mobile',
  elementId: string,
) {
  const elementInError = standard[ruleId].errorElements[screenType][`#e-${elementId}`];
  if (!elementInError) {
    standard[ruleId].counter[screenType].fixedCount -= 1;
  } else {
    switch (elementInError.status) {
      case 'fixed':
        standard[ruleId].counter[screenType].fixedCount -= 1;
        break;
      case 'skip':
        standard[ruleId].counter[screenType].skippedCount -= 1;
        break;
      case 'new':
        standard[ruleId].counter[screenType].errorCount -= 1;
    }
  }

  return standard;
}

export const getFonts = (elementMerId: string, type: string) => {
  const prefix = type === 'button' ? 'button' : 'text-edit :first-child';

  const domElement = GemMeterBaseRule.getElement(`${elementMerId} .${prefix}`);

  if (domElement) {
    const fontFamily = getComputedStyle(domElement).fontFamily;
    const fontWeight = getComputedStyle(domElement).fontWeight;
    return { fontFamily, fontWeight };
  }
  return null;
};
